import { Button, Space, Tag, Typography } from "@arco-design/web-react";
import { Eye, RotateCw } from "lucide-react";

import { canUsePermission } from "../../../lib/auth";
import { useCurrentAuthUser } from "../../auth";
import { useResourceProfileRebuildRuntime } from "../runtime/useResourceProfileRebuildRuntime";
import { formatResourceProfileRebuildStatus } from "../service/resourceProfileRebuildService";
import type { ResourceProfileRebuildRunResult } from "../types";

/** 按资源画像重算状态选择结果块样式。 */
function getResultBoxStyle(status: ResourceProfileRebuildRunResult["status"]) {
  if (status === "completed") {
    return {
      background: "var(--color-success-light-1)",
      borderColor: "var(--color-success-light-4)",
    };
  }
  if (status === "planned") {
    return {
      background: "var(--color-primary-light-1)",
      borderColor: "var(--color-primary-light-4)",
    };
  }
  return {
    background: "var(--color-warning-light-1)",
    borderColor: "var(--color-warning-light-4)",
  };
}

const pageShellStyle = {
  display: "grid",
  gap: 16,
  padding: "4px 0 12px",
} as const;

const headerPanelStyle = {
  display: "grid",
  gap: 12,
  padding: 16,
  border: "1px solid #e5e6eb",
  borderRadius: 8,
  background: "#fff",
} as const;

const tableWrapStyle = {
  overflowX: "auto",
  border: "1px solid #e5e6eb",
  borderRadius: 8,
  background: "#fff",
} as const;

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  minWidth: 960,
} as const;

const cellStyle = {
  borderBottom: "1px solid #e5e6eb",
  padding: "12px 14px",
  textAlign: "left",
  verticalAlign: "middle",
} as const;

const headerCellStyle = {
  ...cellStyle,
  background: "#f7f8fa",
  color: "#4e5969",
  fontWeight: 600,
} as const;

/** 资源画像重算页面主体。 */
export function ResourceProfileRebuildFeaturePage() {
  const runtime = useResourceProfileRebuildRuntime();
  const canExecute = canUsePermission(
    useCurrentAuthUser().data,
    "data.resource_profile:execute",
  );
  const latestResultTask = runtime.latestResult
    ? runtime.tasks.find((task) => task.taskKey === runtime.latestResult?.taskKey)
    : undefined;

  return (
    <main style={pageShellStyle}>
      <section style={headerPanelStyle}>
        <div style={{ display: "grid", gap: 6 }}>
          <Typography.Title heading={5} style={{ marginBottom: 0 }}>
            资源画像重算
          </Typography.Title>
          <Typography.Text type="secondary">
            根据新系统行程与需求数据，定时或手动重算考察点画像和住宿点使用统计。
          </Typography.Text>
        </div>
        <Space size={12} wrap>
          <Tag color="arcoblue">模式：第一版全量重算</Tag>
          <Tag color="green">来源：新系统业务数据</Tag>
          <Tag color="orangered">不属于旧系统同步</Tag>
        </Space>
      </section>

      {runtime.errorMessage ? (
        <section
          role="alert"
          style={{
            background: "var(--color-danger-light-1)",
            borderColor: "var(--color-danger-light-4)",
            borderStyle: "solid",
            borderWidth: 1,
            borderRadius: 6,
            padding: 12,
          }}
        >
          {runtime.errorMessage}
        </section>
      ) : null}

      <div style={tableWrapStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={headerCellStyle}>任务</th>
              <th style={headerCellStyle}>说明</th>
              <th style={headerCellStyle}>模式</th>
              <th style={headerCellStyle}>操作</th>
            </tr>
          </thead>
          <tbody>
            {runtime.loading ? (
              <tr>
                <td style={cellStyle} colSpan={4}>
                  正在加载资源画像重算任务...
                </td>
              </tr>
            ) : null}
            {!runtime.loading && runtime.tasks.length === 0 ? (
              <tr>
                <td style={cellStyle} colSpan={4}>
                  暂无资源画像重算任务。
                </td>
              </tr>
            ) : null}
            {!runtime.loading
              ? runtime.tasks.map((task) => {
                  const isPreviewing = runtime.previewingTaskKey === task.taskKey;
                  const isExecuting = runtime.executingTaskKey === task.taskKey;
                  return (
                    <tr key={task.taskKey}>
                      <td style={cellStyle}>{task.taskName}</td>
                      <td style={cellStyle}>{task.description}</td>
                      <td style={cellStyle}>
                        <Tag color="arcoblue">{task.rebuildMode === "full" ? "全量" : task.rebuildMode}</Tag>
                      </td>
                      <td style={cellStyle}>
                        <Space size={8} wrap>
                          <Button
                            icon={<Eye size={16} />}
                            loading={isPreviewing}
                            disabled={!task.supportsPreview || isExecuting}
                            onClick={() => void runtime.handlePreview(task.taskKey)}
                          >
                            预览
                          </Button>
                          <Button
                            type="primary"
                            icon={<RotateCw size={16} />}
                            loading={isExecuting}
                            disabled={!canExecute || !task.supportsExecute || isPreviewing}
                            onClick={() => void runtime.handleExecute(task.taskKey)}
                          >
                            重算
                          </Button>
                        </Space>
                      </td>
                    </tr>
                  );
                })
              : null}
          </tbody>
        </table>
      </div>

      {runtime.latestResult ? (
        <section
          role="status"
          style={{
            ...getResultBoxStyle(runtime.latestResult.status),
            borderStyle: "solid",
            borderWidth: 1,
            borderRadius: 6,
            padding: 12,
          }}
        >
          <div style={{ display: "grid", gap: 4 }}>
            <span>
              执行结果：
              {formatResourceProfileRebuildStatus(runtime.latestResult.status)}
            </span>
            <span>任务：{latestResultTask?.taskName || runtime.latestResult.taskName}</span>
            <span>{runtime.latestResult.message}</span>
            <span>扫描数量：{runtime.latestResult.scannedCount}</span>
            <span>更新数量：{runtime.latestResult.updatedCount}</span>
            <span>删除关系：{runtime.latestResult.relationDeletedCount}</span>
            <span>新增关系：{runtime.latestResult.relationInsertedCount}</span>
          </div>
        </section>
      ) : null}
    </main>
  );
}
